import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Chip,
  Flag,
  Icon,
  ListGroup,
  ListRow,
  Option,
  OptionList,
  SegmentedControl,
  Skeleton,
  TextField,
} from '@uplink/ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { errorFields, errorText, useTRPC } from '../api.ts';
import { MainActionBar } from '../native-controls.tsx';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { providerName } from '../providers.ts';
import { REGIONS, type Region, ram, regionOf } from '../servers-ui.tsx';
import { KeyPicker, useKeyPicker } from '../ssh-keys.tsx';
import { haptic } from '../telegram.ts';
import { VPN_OPTIONS, type VpnKind } from '../vpn.tsx';

type Step = 'vpn' | 'location' | 'plan' | 'image' | null;
type Purpose = 'server' | 'vpn';
const POPULAR_PLANS = 4;

/** A finished step collapsed to one line; tapping it opens the step again. */
function StepSummary({
  label,
  value,
  leading,
  onEdit,
  error,
}: {
  label: string;
  value: ReactNode;
  leading?: ReactNode;
  onEdit: () => void;
  error?: string;
}) {
  const { t } = usePrefs();
  return (
    <button
      type="button"
      className={`ul-step-summary${error ? ' ul-step-summary--error' : ''}`}
      onClick={onEdit}
      aria-invalid={!!error || undefined}
    >
      {leading}
      <span className="ul-step-summary__body">
        <span className="ul-step-summary__label">{label}</span>
        <span className="ul-step-summary__value">{value}</span>
        {error ? <span className="ul-step-summary__error">{error}</span> : null}
      </span>
      <span className="ul-step-summary__action">{t.common.change}</span>
    </button>
  );
}

export function ServerCreateScreen({ nav, vpn: initialVpn }: { nav: Nav; vpn?: boolean }) {
  const trpc = useTRPC();
  const { t, money, countryName } = usePrefs();
  const queryClient = useQueryClient();
  const offers = useQuery(trpc.servers.offers.queryOptions());
  const images = useQuery(trpc.servers.images.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const me = useQuery(trpc.me.queryOptions());

  const [purpose, setPurpose] = useState<Purpose>(initialVpn ? 'vpn' : 'server');
  const [vpn, setVpn] = useState<VpnKind | null>(null);
  const [step, setStep] = useState<Step>(initialVpn ? 'vpn' : 'location');
  const [region, setRegion] = useState<Region>('europe');
  const [location, setLocation] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [allPlans, setAllPlans] = useState(false);
  const [family, setFamily] = useState<string | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const keyPicker = useKeyPicker();

  // Для VPN показываются только облака, которые администратор разрешил использовать под VPN
  const available = useMemo(
    () =>
      (offers.data ?? [])
        .map((o) => {
          const plans =
            purpose === 'vpn'
              ? o.plans.filter(
                  (p) =>
                    p.vpn &&
                    (images.data ?? []).some(
                      (image) =>
                        image.provider === p.provider &&
                        image.vpnSupported &&
                        (!p.images || p.images.includes(image.id)),
                    ),
                )
              : o.plans;
          return { ...o, plans, fromCents: Math.min(...plans.map((p) => p.priceCents)) };
        })
        .filter((o) => o.plans.length),
    [images.data, offers.data, purpose],
  );
  const regions = useMemo(
    () => REGIONS.filter((r) => available.some((o) => regionOf(o.country) === r)),
    [available],
  );
  useEffect(() => {
    if (regions.length && !regions.includes(region)) setRegion(regions[0]!);
  }, [regions, region]);

  const offer = available.find((o) => o.location === location);
  const plan = offer?.plans.find((p) => p.id === planId);
  // Для VPN API помечает только проверенные образы, чтобы клиент не повторял серверное правило версий.
  const planImages = (images.data ?? []).filter(
    (i) =>
      (!plan || i.provider === plan.provider) &&
      (!plan?.images || plan.images.includes(i.id)) &&
      (purpose !== 'vpn' || i.vpnSupported),
  );
  const families = [...new Map(planImages.map((i) => [i.family, i.name.replace(/\s[\d.]+$/, '')])).entries()];
  const versions = planImages.filter((i) => i.family === family);
  const image = planImages.find((i) => i.id === imageId);

  // Keep the system valid for the chosen plan: same family if it has it, else the first available
  useEffect(() => {
    if (!planImages.length || planImages.some((i) => i.id === imageId)) return;
    const sameFamily = planImages.filter((i) => i.family === family);
    // Ubuntu 24.04 проверена для обычных серверов и позволяет позднее установить VPN.
    const next =
      sameFamily.find((i) => i.vpnSupported) ??
      sameFamily[0] ??
      planImages.find((i) => i.vpnSupported) ??
      planImages[0]!;
    setFamily(next.family);
    setImageId(next.id);
  }, [planImages, imageId, family]);

  const order = useMutation(
    trpc.servers.order.mutationOptions({
      onSuccess: (r) => {
        haptic.success();
        queryClient.invalidateQueries(trpc.servers.pathFilter());
        queryClient.invalidateQueries(trpc.balance.pathFilter());
        nav.replace({ name: 'server', id: r.id });
      },
      onError: () => haptic.error(),
    }),
  );
  const invalidFields = errorFields(order.error);
  const invalid = t.errors.fields;
  const editStep = (next: Exclude<Step, null>) => {
    order.reset();
    setStep(next);
  };

  if (offers.isPending || images.isPending) {
    return (
      <main className="ul-screen">
        <h1 className="ul-screen__title">{t.serverCreate.title}</h1>
        <Skeleton height={44} />
        <Skeleton height={320} />
      </main>
    );
  }
  if (offers.isError || images.isError || !offers.data?.length) {
    return (
      <main className="ul-screen">
        <h1 className="ul-screen__title">{t.serverCreate.title}</h1>
        <div className="ul-error" role="alert">
          {offers.isError || images.isError
            ? errorText(offers.error ?? images.error)
            : t.serverCreate.loadingPlans}
        </div>
      </main>
    );
  }

  const cities = available
    .filter((o) => regionOf(o.country) === region)
    .sort((a, b) => a.fromCents - b.fromCents);
  const plans = offer ? (allPlans ? offer.plans : offer.plans.slice(0, POPULAR_PLANS)) : [];
  // Next step after the open one: skip steps that already have a valid choice
  const advance = () => {
    haptic.select();
    if (step === 'vpn') setStep(offer ? (plan ? (image ? null : 'image') : 'plan') : 'location');
    else if (step === 'location') setStep(plan ? (image ? null : 'image') : 'plan');
    else if (step === 'plan') setStep(image ? null : 'image');
    else setStep(null);
  };
  const stepReady =
    (step === 'vpn' && !!vpn) ||
    (step === 'location' && !!offer) ||
    (step === 'plan' && !!plan) ||
    (step === 'image' && !!image);
  const shortBy = plan && balance.data ? Math.max(0, plan.priceCents - balance.data.balanceCents) : 0;

  const pickLocation = (id: string) => {
    haptic.select();
    setLocation(id);
    const o = available.find((x) => x.location === id)!;
    if (!o.plans.some((p) => p.id === planId)) setPlanId(o.plans[0]?.id ?? null);
    setAllPlans(false);
    setStep('plan');
  };

  return (
    <main className="ul-screen ul-screen--with-action">
      <h1 className="ul-screen__title">{t.serverCreate.title}</h1>

      <SegmentedControl<Purpose>
        label={t.serverCreate.purpose}
        value={purpose}
        onChange={(p) => {
          haptic.select();
          setPurpose(p);
          if (p === 'vpn' && !vpn) setStep('vpn');
          if (p === 'server' && step === 'vpn') setStep(offer ? (plan ? null : 'plan') : 'location');
        }}
        options={[
          { value: 'server', label: t.serverCreate.purposeServer },
          { value: 'vpn', label: t.serverCreate.purposeVpn },
        ]}
      />

      {/* 0. Which VPN */}
      {purpose === 'vpn' ? (
        step === 'vpn' || !vpn ? (
          <section className="ul-stack" aria-labelledby="srv-vpn">
            <aside className="ul-vpn-benefits" aria-labelledby="srv-vpn-benefits">
              <span className="ul-vpn-benefits__icon" aria-hidden>
                <Icon name="shield" size={22} />
              </span>
              <div>
                <h2 id="srv-vpn-benefits">{t.serverCreate.vpnBenefitsTitle}</h2>
                <ul>
                  {t.serverCreate.vpnBenefits.map((benefit) => (
                    <li key={benefit}>
                      <Icon name="check" size={15} weight="bold" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
            <Button
              variant="secondary"
              size="l"
              block
              icon="shield"
              onClick={() => nav.push({ name: 'vpnTrial' })}
            >
              {t.serverCreate.trialButton}
            </Button>
            <h2 className="ul-group__header" id="srv-vpn">
              {t.serverCreate.vpnWhich}
            </h2>
            <OptionList label={t.serverCreate.vpnWhich}>
              {VPN_OPTIONS.map((o) => (
                <Option
                  key={o.kind}
                  selected={o.kind === vpn}
                  onSelect={() => {
                    haptic.select();
                    setVpn(o.kind);
                    setStep(offer ? (plan ? (image ? null : 'image') : 'plan') : 'location');
                  }}
                  leading={
                    <span className="ul-step-icon">
                      <Icon name="shield" size={20} />
                    </span>
                  }
                  title={o.name}
                  subtitle={t.vpn.about[o.kind]}
                  trailing={o.recommended ? <Badge tone="accent">{t.vpn.recommended}</Badge> : undefined}
                />
              ))}
            </OptionList>
            <div className="ul-group__footer">{t.serverCreate.vpnFooter}</div>
          </section>
        ) : (
          <StepSummary
            label={t.serverCreate.vpnShort}
            leading={
              <span className="ul-step-icon">
                <Icon name="shield" size={20} />
              </span>
            }
            value={VPN_OPTIONS.find((o) => o.kind === vpn)!.name}
            onEdit={() => editStep('vpn')}
            error={invalidFields.has('vpn') ? invalid : undefined}
          />
        )
      ) : null}

      {/* 1. Where */}
      {purpose === 'vpn' && !vpn ? null : step === 'location' || !offer ? (
        <section className="ul-stack" aria-labelledby="srv-where">
          <h2 className="ul-group__header" id="srv-where">
            {t.serverCreate.where}
          </h2>
          {regions.length > 1 ? (
            <SegmentedControl<Region>
              label={t.serverCreate.region}
              value={region}
              onChange={(r) => {
                haptic.select();
                setRegion(r);
              }}
              options={regions.map((r) => ({ value: r, label: t.servers.regions[r] }))}
            />
          ) : null}
          {cities.length === 0 ? (
            <div className="ul-group__footer">{t.serverCreate.vpnUnavailable}</div>
          ) : null}
          <OptionList label={t.serverCreate.city}>
            {cities.map((o) => {
              const providers = [...new Set(o.plans.map((plan) => providerName(plan.provider)))];
              const subtitle = me.data?.isAdmin
                ? `${countryName(o.country)} · ${providers.join(', ')}`
                : countryName(o.country);

              return (
                <Option
                  key={o.location}
                  selected={o.location === location}
                  onSelect={() => pickLocation(o.location)}
                  leading={<Flag country={o.country} size={32} />}
                  title={o.city}
                  subtitle={subtitle}
                  trailing={
                    <>
                      {t.common.from(money(o.fromCents))}
                      <small>{t.common.perMonth}</small>
                    </>
                  }
                />
              );
            })}
          </OptionList>
        </section>
      ) : (
        <StepSummary
          label={t.serverCreate.whereShort}
          leading={<Flag country={offer.country} size={32} />}
          value={`${offer.city}, ${countryName(offer.country)}`}
          onEdit={() => editStep('location')}
          error={invalidFields.has('planId') ? invalid : undefined}
        />
      )}

      {/* 2. How powerful */}
      {offer ? (
        step === 'plan' || !plan ? (
          <section className="ul-stack" aria-labelledby="srv-plan">
            <h2 className="ul-group__header" id="srv-plan">
              {t.serverCreate.power}
            </h2>
            <OptionList label={t.serverCreate.plan}>
              {plans.map((p) => (
                <Option
                  key={p.id}
                  selected={p.id === planId}
                  onSelect={() => {
                    haptic.select();
                    setPlanId(p.id);
                    setStep('image');
                  }}
                  title={`${p.cpu} vCPU · ${ram(p.ramMb, t)}`}
                  subtitle={t.servers.disk(p.diskGb)}
                  trailing={
                    <>
                      {money(p.priceCents)}
                      <small>{t.common.perMonth}</small>
                    </>
                  }
                />
              ))}
            </OptionList>
            {!allPlans && offer.plans.length > POPULAR_PLANS ? (
              <button type="button" className="ul-link" onClick={() => setAllPlans(true)}>
                {t.serverCreate.allPlans(offer.plans.length)}
              </button>
            ) : null}
          </section>
        ) : (
          <StepSummary
            label={t.serverCreate.power}
            leading={
              <span className="ul-step-icon">
                <Icon name="cpu" size={20} />
              </span>
            }
            value={`${plan.cpu} vCPU · ${ram(plan.ramMb, t)} · ${plan.diskGb} ${t.servers.gb}`}
            onEdit={() => editStep('plan')}
            error={invalidFields.has('planId') ? invalid : undefined}
          />
        )
      ) : null}

      {/* 3. Which system */}
      {plan ? (
        step === 'image' || !image ? (
          <section className="ul-stack" aria-labelledby="srv-os">
            <h2 className="ul-group__header" id="srv-os">
              {t.serverCreate.system}
            </h2>
            <div className="ul-chips">
              {families.map(([f, label]) => (
                <Chip
                  key={f}
                  selected={f === family}
                  onClick={() => {
                    haptic.select();
                    order.reset();
                    setFamily(f);
                    const familyImages = planImages.filter((i) => i.family === f);
                    setImageId((familyImages.find((i) => i.vpnSupported) ?? familyImages[0]!).id);
                  }}
                >
                  {label}
                </Chip>
              ))}
            </div>
            {versions.length > 1 ? (
              <SegmentedControl
                label={t.serverCreate.version}
                value={imageId ?? ''}
                onChange={(v) => {
                  haptic.select();
                  order.reset();
                  setImageId(v);
                }}
                options={versions.map((v) => ({ value: v.id, label: v.name.replace(/^.*\s/, '') }))}
              />
            ) : null}
          </section>
        ) : (
          <StepSummary
            label={t.serverCreate.system}
            leading={
              <span className="ul-step-icon">
                <Icon name="terminal" size={20} />
              </span>
            }
            value={image.name}
            onEdit={() => editStep('image')}
            error={invalidFields.has('imageId') ? invalid : undefined}
          />
        )
      ) : null}

      {purpose === 'server' && image && !image.vpnSupported ? (
        <div className="ul-mon-intro" role="note">
          <Icon name="info" size={22} />
          <span>{t.serverCreate.vpnLaterRequiresUbuntu}</span>
        </div>
      ) : null}

      {/* 4. Details and total */}
      {plan && image && step === null ? (
        <>
          <section className="ul-form" aria-label={t.serverCreate.details}>
            <TextField
              id="srv-name"
              label={t.serverCreate.name}
              placeholder={t.serverCreate.namePlaceholder}
              value={name}
              onChange={(value) => {
                order.reset();
                setName(value);
              }}
              hint={t.serverCreate.nameHint}
              error={invalidFields.has('name') ? invalid : undefined}
            />
            <KeyPicker picker={keyPicker} error={invalidFields.has('sshPublicKey') ? invalid : undefined} />
          </section>

          <ListGroup footer={t.serverCreate.billingFooter}>
            <ListRow title={t.serverCreate.monthly} value={money(plan.priceCents)} />
            {balance.data ? (
              <ListRow title={t.serverCreate.onBalance} value={money(balance.data.balanceCents)} />
            ) : null}
          </ListGroup>
        </>
      ) : null}

      {order.isError ? (
        <div className="ul-error" role="alert">
          {errorText(order.error)}
        </div>
      ) : null}

      <MainActionBar
        action={
          step !== null
            ? stepReady
              ? { text: t.common.continue, onClick: advance }
              : null
            : !plan || !image || (purpose === 'vpn' && !vpn)
              ? null
              : shortBy > 0
                ? {
                    text: t.common.topUpBy(money(Math.max(shortBy, 500))),
                    onClick: () => {
                      void keyPicker.remember();
                      nav.push({
                        name: 'topup',
                        amountCents: Math.max(shortBy, 500),
                        after: {
                          kind: 'serverOrder',
                          planId: plan.id,
                          imageId: image.id,
                          name: name.trim(),
                          sshPublicKey: keyPicker.publicKey,
                          vpn: purpose === 'vpn' ? (vpn ?? undefined) : undefined,
                          label: name.trim() || plan.id,
                        },
                      });
                    },
                  }
                : {
                    text: t.serverCreate.createFor(money(plan.priceCents)),
                    onClick: () => {
                      void keyPicker.remember();
                      order.mutate({
                        planId: plan.id,
                        imageId: image.id,
                        name: name.trim(),
                        sshPublicKey: keyPicker.publicKey,
                        vpn: purpose === 'vpn' ? (vpn ?? undefined) : undefined,
                      });
                    },
                    loading: order.isPending,
                    disabled: !balance.data,
                  }
        }
      />
    </main>
  );
}
